import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Building2, Lock, Mail } from "lucide-react";
import { toast } from "sonner";
import AuthService from "@/services/APIs/AuthService";
import {
  setLocalAccessToken,
  setLocalRefreshToken,
  setLocalUserInfo,
  getAccessToken,
} from "@/lib/storeManager";

const PP_Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Check if already logged in
  useEffect(() => {
    const token = getAccessToken();
    if (token) {
      // Already logged in, redirect to dashboard
      navigate("/property-portal/dashboard", { replace: true });
    }
  }, [navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Use real authentication API
      const response = await AuthService.login({ email, password });

      if (response) {
        // Store tokens and user info
        setLocalAccessToken(response.access);
        setLocalRefreshToken(response.refresh);
        setLocalUserInfo({
          first_name: response.user?.first_name ?? "",
          last_name: response.user?.last_name ?? "",
          email: response.user?.email ?? email,
          user_type: response.user?.user_type ?? "",
        });

        toast.success("Login successful!");
        navigate("/property-portal/dashboard");
      }
    } catch (error: any) {
      console.error("Login error:", error);
      toast.error(
        error?.response?.data?.message ??
          error?.response?.data?.error?.message ??
          "Invalid email or password"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary via-primary/90 to-secondary p-4">
      <Card className="w-full max-w-md shadow-2xl">
        <CardHeader className="space-y-4 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-secondary">
            <Building2 className="h-10 w-10 text-primary-foreground" />
          </div>
          <div>
            <CardTitle className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-3xl font-bold text-transparent">
              Property Investment
            </CardTitle>
            <CardDescription className="mt-2 text-base">
              Sign in to manage your property portfolio
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">
                Email Address
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="h-12 pl-10"
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="h-12 pl-10"
                  required
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="h-12 w-full bg-gradient-to-r from-primary to-secondary text-base font-semibold transition-opacity hover:opacity-90"
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default PP_Login;
